import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider
} from '@mui/material';
import { Delete, Warning } from '@mui/icons-material';
import { motion } from 'framer-motion';
import SuccessDialog from './SuccessDialog';

const DeleteDialog = ({ open, onClose, item, onDelete }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const MotionBox = motion.div;

  const handleDelete = async () => {
    if (!item || !onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(item.item_id);
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error('Error deleting item:', error);
      // Could add error handling here
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    onClose();
  };

  if (!item) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { borderRadius: 2, maxWidth: '450px' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Warning color="error" sx={{ mr: 1 }} />
            <Typography variant="h6">{t('menu.deleteTitle')}</Typography>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent>
          <MotionBox
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Typography gutterBottom>
              {t('menu.deleteConfirm')}
            </Typography>
            
            <Box 
              sx={{ 
                p: 2, 
                my: 2, 
                border: '1px solid', 
                borderColor: 'error.light', 
                borderRadius: 1,
                bgcolor: 'error.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {item.name}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  ${parseFloat(item.price).toFixed(2)} • {t('menu.itemId')}: {item.item_id}
                </Typography>
              </Box>
              
              {item.image && (
                <Box
                  component="img"
                  src={item.image}
                  alt={item.name}
                  sx={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: 1
                  }}
                />
              )}
            </Box>
            
            <Typography color="error.main" variant="body2" sx={{ fontWeight: 500 }}>
              {t('menu.deleteWarning')}
            </Typography>
          </MotionBox>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </Button>
          
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <Delete />}
            disabled={isDeleting}
          >
            {isDeleting ? t('menu.deleting') : t('menu.deleteButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <SuccessDialog 
        open={successDialogOpen} 
        onClose={handleSuccessDialogClose} 
        message={t('menu.deleteSuccess')}
      />
    </>
  );
};

export default DeleteDialog;